import {
  PUBLISH_SCOPE,
  TIKTOK_DIRECT_POST_SCOPE,
  TIKTOK_UPLOAD_SCOPE,
  type PublishConnection,
  type PublishEligibility,
} from './types';
import { PENDING_LIMIT } from './computePendingQuota';

export const YOUTUBE_UPLOAD_SCOPE = 'https://www.googleapis.com/auth/youtube.upload';

/** A TikTok connection can receive a send only when it is active AND carries
 *  either drafts (`video.upload`) or Direct Post (`video.publish`). Every
 *  connection linked before publishing shipped lacks both. */
export function isSendableConnection(connection: PublishConnection): boolean {
  return connection.platform === 'tiktok'
    && connection.status === 'active'
    && (
      connection.scopes.includes(TIKTOK_UPLOAD_SCOPE)
      || connection.scopes.includes(TIKTOK_DIRECT_POST_SCOPE)
      || connection.scopes.includes(PUBLISH_SCOPE)
    );
}

export function isDirectPostConnection(connection: PublishConnection): boolean {
  return connection.platform === 'tiktok'
    && connection.status === 'active'
    && connection.scopes.includes(TIKTOK_DIRECT_POST_SCOPE);
}

/** YouTube connections linked before direct publishing may be active but lack
 * the exact upload grant. Treat those as reconnect-required, not connected. */
export function isYouTubePublishableConnection(connection: PublishConnection): boolean {
  return connection.platform === 'youtube'
    && connection.status === 'active'
    && Array.isArray(connection.scopes)
    && connection.scopes.includes(YOUTUBE_UPLOAD_SCOPE);
}

export const INSTAGRAM_PUBLISH_SCOPE = 'instagram_business_content_publish';

export function isInstagramPublishableConnection(connection: PublishConnection): boolean {
  return connection.platform === 'instagram'
    && connection.status === 'active'
    && Array.isArray(connection.scopes)
    && connection.scopes.includes(INSTAGRAM_PUBLISH_SCOPE);
}

export function resolvePublishEligibility({
  hasFinishedVideo,
  connections,
  pendingCount,
}: {
  hasFinishedVideo: boolean;
  connections: PublishConnection[];
  pendingCount: number;
}): PublishEligibility {
  if (!hasFinishedVideo) return 'not_rendered';

  const tiktok = connections.filter((c) => c.platform === 'tiktok');
  if (tiktok.length === 0) return 'no_connection';
  if (!tiktok.some(isSendableConnection)) return 'reconnect_required';
  if (pendingCount >= PENDING_LIMIT) return 'quota_exceeded';

  return 'eligible';
}

export const FACEBOOK_PUBLISH_SCOPE = 'pages_manage_posts';

/** Only Pages the user can post on carry `pages_manage_posts`. */
export function isFacebookPublishableConnection(connection: PublishConnection): boolean {
  return connection.platform === 'facebook'
    && connection.status === 'active'
    && Array.isArray(connection.scopes)
    && connection.scopes.includes(FACEBOOK_PUBLISH_SCOPE);
}
